import React, { useEffect, useMemo, useState } from "react";
import { Coins, Percent, CalendarDays, DollarSign, TrendingUp, Users, Pickaxe, PiggyBank, Network } from "lucide-react";
import api from "@/lib/api";
import LiveBotRates from "@/components/LiveBotRates";
import Field from "@/components/calculator/Field";
import BotPledgeView from "@/components/calculator/BotPledgeView";
import BotRecoveryView from "@/components/calculator/BotRecoveryView";
import BotLevelView from "@/components/calculator/BotLevelView";
import BotSourceView from "@/components/calculator/BotSourceView";

// The plans the dApp's BOT Pledge screen offers.
const PLANS = [
  { key: "flexible", label: "Flexible", days: 0 },
  { key: "360", label: "360d", days: 360 },
  { key: "540", label: "540d", days: 540 },
  { key: "720", label: "720d", days: 720 },
];
const RATE_HINT = {
  dapp: "CaryPact dApp rate", carypact: "CaryPact plan rate",
  admin: "typed from the dApp", observed: "measured on your own orders",
};
const TABS = [
  { k: "pledge", label: "Pledge growth", short: "Growth", icon: TrendingUp },
  { k: "back", label: "When your BOT is back", short: "Money back", icon: PiggyBank },
  { k: "team", label: "Team level income", short: "Team", icon: Users },
  { k: "sources", label: "Where the BOT comes from", short: "Sources", icon: Pickaxe },
];
const STORAGE_KEY = "bot_calculator_v1";
const TEAL = "#0E7490";
const fmtN = (n, dp) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: dp });

function UnitSwitch({ unit, onChange }) {
  return (
    <span className="inline-flex rounded-lg p-0.5" style={{ background: "rgba(15,18,38,0.06)" }} data-testid="bot-calc-unit-switch">
      {[["usd", "$"], ["bot", "BOT"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`bot-calc-unit-${k}`}
          className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold transition-colors"
          style={unit === k ? { background: TEAL, color: "white" } : { color: "#475569" }}>
          {l}
        </button>
      ))}
    </span>
  );
}

/**
 * The BOT calculator — the same shape as the CA one (inputs on the left, views on
 * the right) but running on BOT's own rate table, which CaryPact does not publish:
 * rows are typed in by an admin or measured off the team's own pledges, and the
 * panel underneath says which. Covers the pledge, getting the pledge back, the
 * level income your team's pledges pay you, and where the BOT comes from.
 */
export default function BotCalculator() {
  const saved = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null") || {}; } catch { return {}; }
  })();
  const [tab, setTab] = useState(TABS.some((t) => t.k === saved.tab) ? saved.tab : "pledge");
  // The pledge is typed in dollars by default (switchable to BOT). Older saves
  // held a BOT amount, so they are carried over as dollars at the saved price.
  const [pledgeUnit, setPledgeUnit] = useState(saved.pledgeUnit === "bot" ? "bot" : "usd");
  const [pledgeAmt, setPledgeAmt] = useState(() => {
    if (saved.pledgeAmt != null) return saved.pledgeAmt;
    if (saved.pledge != null) return +(Number(saved.pledge) * (Number(saved.botPrice) || 12)).toFixed(2);
    return 1000;
  });
  const [ratePct, setRatePct] = useState(saved.ratePct ?? 0.6);
  const [termDays, setTermDays] = useState(saved.termDays ?? 360);
  const [days, setDays] = useState(saved.days ?? 360);
  const [botPrice, setBotPrice] = useState(saved.botPrice ?? 12);
  const [monthlyAppn, setMonthlyAppn] = useState(saved.monthlyAppn ?? 0);
  const [teamPledge, setTeamPledge] = useState(saved.teamPledge ?? 1000);
  const [sharePct, setSharePct] = useState(saved.sharePct ?? 29);
  const [growthPct, setGrowthPct] = useState(saved.growthPct ?? 10);
  const [months, setMonths] = useState(saved.months ?? 12);
  const [caPerDay, setCaPerDay] = useState(saved.caPerDay ?? 10);
  const [caPrice, setCaPrice] = useState(saved.caPrice ?? 22);
  const [hashBotPerDay, setHashBotPerDay] = useState(saved.hashBotPerDay ?? 0);
  const [minerBotPerDay, setMinerBotPerDay] = useState(saved.minerBotPerDay ?? 0);
  const [rateData, setRateData] = useState(null);
  const [touched, setTouched] = useState(!!saved.pledge || saved.pledgeAmt != null);

  // One call brings the BOT plan table, both live prices, the measured level share
  // and what the team's bought hashrate actually pays.
  useEffect(() => {
    api.get("/bot-staking-rates").then(({ data }) => {
      setRateData(data);
      if (data?.bot_price) setBotPrice(+Number(data.bot_price).toFixed(2));
      if (data?.ca_price) setCaPrice(+Number(data.ca_price).toFixed(2));
      // Saves from before the dApp plan table held a measured rate (and maybe a
      // 30/90/180 term the dApp doesn't offer), so snap those onto a real plan once.
      if (!touched || saved.rv !== 2) {
        const k = Number(termDays) ? String(termDays) : "flexible";
        const known = data?.rates?.[k] != null;
        const best = known ? data.rates[k] : data?.rates?.["360"];
        if (best) setRatePct(best);
        if (!known && data?.rates?.["360"] != null) setTermDays(360);
      }
      if (!touched) {
        if (data?.level_share?.pct != null) setSharePct(data.level_share.pct);
        if (data?.hashrate?.bot_per_day != null) setHashBotPerDay(+Number(data.hashrate.bot_per_day).toFixed(4));
      }
    }).catch(() => { /* the panel just stays on the saved numbers */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        tab, pledgeAmt, pledgeUnit, ratePct, termDays, days, botPrice, monthlyAppn, teamPledge, sharePct,
        growthPct, months, caPerDay, caPrice, hashBotPerDay, minerBotPerDay, rv: 2,
      }));
    } catch { /* ignore quota / disabled storage */ }
  }, [tab, pledgeAmt, pledgeUnit, ratePct, termDays, days, botPrice, monthlyAppn, teamPledge, sharePct,
    growthPct, months, caPerDay, caPrice, hashBotPerDay, minerBotPerDay]);

  // Every view below runs on BOT, so a dollar pledge is converted at the BOT price.
  const price = Number(botPrice) || 0;
  const pledge = pledgeUnit === "usd" ? (price > 0 ? (Number(pledgeAmt) || 0) / price : 0) : (Number(pledgeAmt) || 0);
  const switchUnit = (u) => {
    if (u === pledgeUnit) return;
    const a = Number(pledgeAmt) || 0;
    setPledgeAmt(u === "bot" ? (price > 0 ? +(a / price).toFixed(4) : 0) : +(a * price).toFixed(2));
    setPledgeUnit(u);
    setTouched(true);
  };

  const plans = useMemo(() => PLANS
    .map((p) => ({ ...p, rate: Number(rateData?.rates?.[p.key]) }))
    .filter((p) => p.rate > 0 && !Number.isNaN(p.rate)), [rateData]);
  const teamRate = Number(rateData?.rates?.["360"] ?? ratePct) || 0;
  const activePlan = plans.find((p) => Number(ratePct) === p.rate && Number(termDays) === p.days);
  const rateHint = activePlan ? RATE_HINT[rateData?.sources?.[activePlan.key]] : "your own number";
  const mark = (fn) => (v) => { setTouched(true); fn(v); };
  const showPledge = tab === "pledge" || tab === "back" || tab === "sources";

  return (
    <section className="px-6 md:px-10 pt-2 pb-6" data-testid="bot-calculator">
      <div className="max-w-[1400px] mx-auto">
        <div className="pill mb-4" style={{ borderColor: "rgba(14,116,144,0.3)" }} data-testid="bot-calc-pill">
          <span className="dot" style={{ background: TEAL }} /> BOT pledge calculator · {rateData?.source === "carypact"
            ? "CaryPact's own BOT rates"
            : rateData?.source === "admin" ? "rates typed from the dApp"
              : rateData?.source === "dapp" ? "CaryPact dApp BOT rates" : "rates measured off your own pledges"}
        </div>
        <h1 className="font-display text-3xl md:text-5xl font-semibold leading-[1.1] tracking-tight">
          Everything BOT does, <span style={{ color: TEAL }}>on its own numbers</span>.
        </h1>
        <p className="text-base text-ink-2 max-w-2xl mt-3 mb-6">
          The pledge, the principal that unlocks every day, the level income your team&apos;s pledges pay you,
          and where the BOT comes from in the first place.
        </p>

        <div className="grid lg:grid-cols-[380px_1fr] gap-6">
          <div className="space-y-4">
            <div className="glass-strong p-6 space-y-4 h-fit" data-testid="bot-calc-inputs">
              {showPledge && (
                <>
                  <div className="font-display text-sm font-bold uppercase tracking-wider text-ink-2">Your pledge</div>
                  <Field label="Pledge amount" icon={pledgeUnit === "usd" ? DollarSign : Coins} value={pledgeAmt}
                    onChange={mark(setPledgeAmt)} suffix={pledgeUnit === "usd" ? "USD" : "BOT"} accent={TEAL} testid="bot-calc-pledge"
                    labelRight={<UnitSwitch unit={pledgeUnit} onChange={switchUnit} />} />
                  <div className="text-[11px] text-ink-3 font-mono -mt-2.5" data-testid="bot-calc-pledge-converted">
                    {pledgeUnit === "usd" ? `≈ ${fmtN(pledge, 4)} BOT` : `≈ $${fmtN(pledge * price, 2)}`} at ${fmtN(price, 2)}/BOT
                  </div>
                  <Field label="Daily rate" icon={Percent} value={ratePct} onChange={mark(setRatePct)} suffix="%/day"
                    hint={rateHint} accent={TEAL} testid="bot-calc-rate" />
                  <Field label="Term (0 = flexible)" icon={CalendarDays} value={termDays} onChange={mark(setTermDays)} suffix="days" accent={TEAL} testid="bot-calc-term" />
                  <Field label="Show me" icon={CalendarDays} value={days} onChange={mark(setDays)} suffix="days" accent={TEAL} testid="bot-calc-days" />
                  {plans.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3 mb-1.5">BOT plan · sets rate + term</div>
                      <div className="flex gap-1.5 flex-wrap" data-testid="bot-calc-plan-chips">
                        {plans.map((p) => {
                          const active = Number(ratePct) === p.rate && Number(termDays) === p.days;
                          return (
                            <button key={p.key} type="button" data-testid={`bot-calc-plan-${p.key}`}
                              onClick={() => { setTouched(true); setRatePct(p.rate); setTermDays(p.days); if (p.days) setDays(p.days); }}
                              className="px-2.5 py-1 rounded-lg text-[11px] font-mono font-semibold transition-colors"
                              style={active
                                ? { background: TEAL, color: "white", border: `1px solid ${TEAL}` }
                                : { background: "rgba(255,255,255,0.55)", color: "#334155", border: "1px solid rgba(15,18,38,0.08)" }}>
                              {p.label} · {p.rate}%
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="h-px" style={{ background: "rgba(15,18,38,0.08)" }} />
                </>
              )}

              {tab === "team" && (
                <>
                  <div className="font-display text-sm font-bold uppercase tracking-wider text-ink-2 flex items-center gap-1.5">
                    <Users size={14} style={{ color: TEAL }} /> Your team
                  </div>
                  <Field label="BOT pledged by your team" icon={Coins} value={teamPledge} onChange={mark(setTeamPledge)} suffix="BOT" accent={TEAL} testid="bot-calc-team-pledge" />
                  <Field label="Your share of their rewards" icon={Percent} value={sharePct} onChange={mark(setSharePct)} suffix="%"
                    hint={rateData?.level_share?.pct != null ? `yours has run at ${rateData.level_share.pct}%` : "read it off the dApp"} accent={TEAL} testid="bot-calc-share" />
                  <Field label="Team grows by" icon={TrendingUp} value={growthPct} onChange={mark(setGrowthPct)} suffix="%/mo" accent={TEAL} testid="bot-calc-growth" />
                  <Field label="Over" icon={CalendarDays} value={months} onChange={mark(setMonths)} suffix="months" accent={TEAL} testid="bot-calc-months" />
                  <div className="h-px" style={{ background: "rgba(15,18,38,0.08)" }} />
                </>
              )}

              {tab === "sources" && (
                <>
                  <div className="font-display text-sm font-bold uppercase tracking-wider text-ink-2 flex items-center gap-1.5">
                    <Network size={14} style={{ color: "#B45309" }} /> BOT coming in
                  </div>
                  <Field label="CA mined per day" icon={Pickaxe} value={caPerDay} onChange={mark(setCaPerDay)} suffix="CA" accent={TEAL} testid="bot-calc-ca-per-day" />
                  <Field label="CA price" icon={DollarSign} value={caPrice} onChange={mark(setCaPrice)} suffix="USD" accent={TEAL} testid="bot-calc-ca-price" />
                  <Field label="BOT hashrate mining" icon={Pickaxe} value={hashBotPerDay} onChange={mark(setHashBotPerDay)} suffix="BOT/day"
                    hint={rateData?.hashrate?.bot_per_day != null ? "your wallets' median" : undefined} accent={TEAL} testid="bot-calc-hash-bot" />
                  <Field label="Miner rewards (static + dividend)" icon={Coins} value={minerBotPerDay} onChange={mark(setMinerBotPerDay)} suffix="BOT/day"
                    hint="type yours" accent={TEAL} testid="bot-calc-miner-bot" />
                  <div className="h-px" style={{ background: "rgba(15,18,38,0.08)" }} />
                </>
              )}

              <div className="font-display text-sm font-bold uppercase tracking-wider text-ink-2 flex items-center gap-1.5">
                <DollarSign size={14} style={{ color: "#7C3AED" }} /> BOT price
              </div>
              <Field label={rateData?.bot_price ? "BOT price (live)" : "BOT price"} icon={DollarSign} value={botPrice} onChange={mark(setBotPrice)} suffix="USD" accent={TEAL} testid="bot-calc-bot-price" />
              {tab === "pledge" && (
                <Field label="Monthly BOT appreciation" icon={TrendingUp} value={monthlyAppn} onChange={mark(setMonthlyAppn)} suffix="%/mo" accent={TEAL} testid="bot-calc-appn" />
              )}
            </div>
            <LiveBotRates data={rateData} onSaved={setRateData} />
          </div>

          <div>
            <div className="glass p-1.5 mb-5 inline-flex gap-1 flex-wrap" data-testid="bot-calc-tabs">
              {TABS.map((t) => {
                const active = tab === t.k;
                const Icon = t.icon;
                return (
                  <button key={t.k} type="button" onClick={() => setTab(t.k)} data-testid={`bot-calc-tab-${t.k}`}
                    className="px-3.5 py-2 rounded-xl text-[12px] font-semibold transition-colors inline-flex items-center gap-1.5"
                    style={active ? { background: TEAL, color: "white" } : { background: "transparent", color: "#475569" }}>
                    <Icon size={13} />
                    <span className="hidden sm:inline">{t.label}</span>
                    <span className="sm:hidden">{t.short}</span>
                  </button>
                );
              })}
            </div>

            {tab === "pledge" && (
              <BotPledgeView pledge={pledge} ratePct={ratePct} termDays={termDays} days={days}
                botPrice={botPrice} monthlyAppn={monthlyAppn} />
            )}
            {tab === "back" && (
              <BotRecoveryView pledge={pledge} ratePct={ratePct} termDays={termDays} days={days} botPrice={botPrice} />
            )}
            {tab === "team" && (
              <BotLevelView teamPledge={teamPledge} teamRatePct={teamRate} sharePct={sharePct}
                growthPct={growthPct} months={months} botPrice={botPrice} levelShare={rateData?.level_share} />
            )}
            {tab === "sources" && (
              <BotSourceView caPerDay={caPerDay} caPrice={caPrice} botPrice={botPrice}
                hashBotPerDay={hashBotPerDay} minerBotPerDay={minerBotPerDay} ratePct={ratePct}
                days={days} pledge={pledge} hashrateObserved={rateData?.hashrate} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
